<template>
  <div>
    <form action="#" class="slo-auth-login" v-stack-y:sm @submit.prevent="handleLogin">
      <SloField>
        <SloLabel :error="error">{{ 'Email' | t }}</SloLabel>
        <SloInput
          v-model="form.email"
          :error="error"
          ref="input"
          id="email"
          name="email"
          type="email"
          inputmode="email"
        />
      </SloField>
      <SloField>
        <SloLabel :error="error">{{ 'Password' | t }}</SloLabel>
        <SloInput
          v-model="form.password"
          :error="error"
          name="password"
          type="password"
        />
      </SloField>
      <div>
        <SloLink :to="{name :'marketplace-forgot-password'}" class="forgot-password">
          {{ 'Forgot password?' | t }}
        </SloLink>
      </div>
      <SloButton :disabled="isSubmitDisabled" type="submit">
        <SloLoader :loading="loading" color="white" size="md">
          {{ 'Sign In' | t }}
        </SloLoader>
      </SloButton>
    </form>
    <transition name="fade-up">
      <SloModal v-if="clickwrapModalOpen" :max-width="500" :form="false" @dismiss="handleCloseModal">
        <template v-slot:title>{{ 'Terms and Conditions' | t }}</template>
        <div>
          <p>{{ 'Please review and accept the terms and conditions.' | t }}</p>
          <div id="clickwrap-container-auth" class="mt-md"></div>
        </div>
        <template v-slot:footer>
          <SloButton :disabled="!clickwrapValidated" @click="handleClickwrapAccept" class="flex-1">
            {{ 'Continue' | t }}
          </SloButton>
        </template>
      </SloModal>
    </transition>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import t from '../../js/main/i18n';
import {initializeClickwrap, getClickwrapAccessId} from '../../js/main/utils';
import SloField from './SloField.vue';
import SloButton from './SloButton.vue';
import SloLabel from './SloLabel.vue';
import SloInput from './SloInput.vue';
import SloLink from './SloLink.vue';
import SloLoader from './SloLoader.vue';
import SloModal from './SloModal.vue';

export default {
  name: 'SloAuthLogin',
  components: {
    SloLoader,
    SloField,
    SloButton,
    SloLabel,
    SloInput,
    SloLink,
    SloModal
  },
  filters: {
    t
  },
  props: {
    email: {type: String, default: ''}
  },
  data() {
    return {
      loading: false,
      error: false,
      clickwrapGroup: 'sloan-registration',
      clickwrapValidated: false,
      clickwrapModalOpen: false,
      form: {email: '', password: ''}
    };
  },
  watch: {
    'form.email'() {
      this.error = false;
    },
    'form.password'() {
      this.error = false;
    }
  },
  mounted() {
    this.form.email = this.email;
    this.$nextTick(() => this.$refs.input.setFocus());
  },
  computed: {
    ...mapGetters('auth', ['authenticated']),
    isSubmitDisabled() {
      return this.loading
        || !this.form.email
        || !this.form.password;
    }
  },
  methods: {
    ...mapActions('auth', ['login']),
    async handleLogin() {
      this.loading = true;
      this.error = false;
      let clickwrapAcceptanceRequired = false;

      if (window._ps && window._ps.getByKey(this.clickwrapGroup)) {
        await fetch('https://pactsafe.io/latest'
          + '?sid=' + getClickwrapAccessId()
          + '&gkey=' + this.clickwrapGroup
          + '&sig=' + this.form.email
        )
          .then(response => response.json())
          .then(agreements => {
            for (const agreement in agreements) {
              if (!agreements[agreement]) clickwrapAcceptanceRequired = true;
            }
          })
          .catch(() => { return true; });
      }

      if (clickwrapAcceptanceRequired) {
        initializeClickwrap(this.clickwrapGroup, 'clickwrap-container-auth');

        if (window._ps) {
          window._ps.on('valid', () => {
            window._ps('send', 'updated', {custom_data: {site: 'Sloan Marketplace'}});
            this.clickwrapValidated = true;
          });
          window._ps.on('unchecked', () => {
            this.clickwrapValidated = false;
            window._ps(`${this.clickwrapGroup}:send`, 'disagreed');
          });
        } else {
          this.clickwrapValidated = true;
        }

        this.loading = false;
        this.clickwrapModalOpen = true;
        return false;
      }

      await this.login(this.form)
        .then(() => this.$emit('success'))
        .catch(e => {
          this.loading = false;
          this.error = e?.response?.data?.error === 'invalid_grant';

          let errorMessage = 'Invalid email or password.';
          if (e?.response?.data?.error_description !== 'Bad credentials') {
            errorMessage = e?.response?.data?.error_description;
          }
          this.$snotify.error(t(errorMessage));
        });
    },
    handleCloseModal() {
      this.loading = false;
      this.clickwrapModalOpen = false;
    },
    async handleClickwrapAccept() {
      this.clickwrapModalOpen = false;
      await this.handleLogin();
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../scss/system';

.slo-auth-login {
  width: 100%;
}

.slo-button {
  width: 100%;
}

.flex {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

:deep(div.ps-contract) {
  padding-bottom: 0 !important;
}
</style>
